import {useCallback} from 'react';

import * as BenchlingUtil from 'BenchlingUtil';
import copyToClipboard from 'util/clipboard/copyToClipboard';

import {DEFAULT_NAVIGATION_PARAM} from './useHashNavigation';

interface UseLinkGenerationOptions {
  /**
   * Hash parameter name for link generation.
   * Defaults to 'anchor' for consistent URL structure.
   */
  hashParam?: string;

  /**
   * Base URL for link generation.
   * Defaults to current page URL without hash.
   */
  baseUrl?: string;

  /**
   * Toast message to show when link is copied.
   * Defaults to "Link copied to clipboard!"
   */
  toastText?: string;
}

interface UseLinkGenerationResult {
  generateLink: (anchorValue: string) => string;
  copyLinkToClipboard: (anchorValue: string, event?: React.MouseEvent) => void;
}

/**
 * Hook for generating and copying links to anchor sections.
 *
 * Uses 'anchor' as the default hash parameter for consistent URL structure.
 * Can be customized to use other parameters when needed.
 *
 * @param options Configuration for link generation behavior
 * @returns Link generation utilities
 */
export function useLinkGeneration(options: UseLinkGenerationOptions = {}): UseLinkGenerationResult {
  const {hashParam = DEFAULT_NAVIGATION_PARAM, baseUrl, toastText = 'Link copied to clipboard!'} = options;

  /**
   * Generates a deep link URL with the specified anchor value.
   *
   * @param anchorValue The value to use in the hash parameter
   * @returns Complete URL with hash parameter
   */
  const generateLink = useCallback(
    (anchorValue: string): string => {
      const currentBaseUrl = baseUrl ?? window.location.href.split('#')[0];
      const hashParams = new URLSearchParams(BenchlingUtil.getLocationHash());
      hashParams.set(hashParam, anchorValue);
      return `${currentBaseUrl}#${hashParams.toString()}`;
    },
    [hashParam, baseUrl]
  );

  /**
   * Copies a link to the clipboard with the specified anchor value.
   * Shows a toast notification on success.
   *
   * @param anchorValue The value to use in the hash parameter
   * @param event Optional event to stop propagation
   */
  const copyLinkToClipboard = useCallback(
    (anchorValue: string, event?: React.MouseEvent): void => {
      event?.stopPropagation();
      const deepLink = generateLink(anchorValue);
      copyToClipboard(deepLink, {
        showToastOnSuccess: true,
        toastText,
      });
    },
    [generateLink, toastText]
  );

  return {
    generateLink,
    copyLinkToClipboard,
  };
}
